import { motion } from "framer-motion";
import { Copy } from "lucide-react";
import { toast } from "sonner";

const EMAIL = "bdofficial703@gmail.com";
const PHONE = "+91 99203 70131";
const PHONE_RAW = "+919920370131";

export default function CTAViewfinder() {
  const copy = (value, label) => {
    navigator.clipboard?.writeText(value);
    toast.success(`${label} copied`, { description: value });
  };

  return (
    <section
      id="contact"
      data-testid="cta-viewfinder"
      className="relative overflow-hidden py-36 md:py-44"
    >
      {/* Light leak */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#CBFF3A]/8 blur-[150px]" />

      {/* Content */}
      <div className="relative z-20 mx-auto max-w-[1400px] px-6 text-center md:px-16">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto max-w-4xl font-display text-4xl font-500 leading-[1.05] tracking-tight text-[#ECEAE3] sm:text-6xl lg:text-7xl"
        >
          Ready to <span className="italic text-[#CBFF3A] text-glow">roll</span>{" "}
          the next one?
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mx-auto mt-8 max-w-xl font-body text-base font-300 text-white/60 md:text-lg"
        >
          Let's create something that doesn't just look good, but performs.
          Available for branded content, campaigns and social productions
          worldwide — no forms, no gatekeepers.
        </motion.p>

        <motion.a
          href={`mailto:${EMAIL}`}
          data-testid="cta-start-project"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-12 inline-block rounded-full bg-[#CBFF3A] px-14 py-5 font-hud text-sm uppercase tracking-[0.2em] text-black electric-glow transition-transform duration-300 hover:scale-105 active:scale-95"
        >
          Start a Project
        </motion.a>

        {/* Contact details — email + phone, clearly */}
        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="group border border-white/12 bg-[#0f0f11] p-6 text-left transition-colors duration-300 hover:border-[#CBFF3A]/60">
            <div className="mb-3 font-hud text-[10px] uppercase tracking-[0.25em] text-white/40">
              Email
            </div>
            <a
              data-testid="contact-email"
              href={`mailto:${EMAIL}`}
              className="block break-all font-display text-lg font-600 text-white transition-colors group-hover:text-[#CBFF3A] sm:text-xl"
            >
              {EMAIL}
            </a>
            <button
              data-testid="copy-email-btn"
              onClick={() => copy(EMAIL, "Email")}
              className="mt-3 flex items-center gap-2 font-hud text-[10px] uppercase tracking-widest text-white/40 transition-colors hover:text-white"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>

          <div className="group border border-white/12 bg-[#0f0f11] p-6 text-left transition-colors duration-300 hover:border-[#CBFF3A]/60">
            <div className="mb-3 font-hud text-[10px] uppercase tracking-[0.25em] text-white/40">
              Phone
            </div>
            <a
              data-testid="contact-phone"
              href={`tel:${PHONE_RAW}`}
              className="block font-display text-lg font-600 text-white transition-colors group-hover:text-[#CBFF3A] sm:text-xl"
            >
              {PHONE}
            </a>
            <button
              data-testid="copy-phone-btn"
              onClick={() => copy(PHONE_RAW, "Phone number")}
              className="mt-3 flex items-center gap-2 font-hud text-[10px] uppercase tracking-widest text-white/40 transition-colors hover:text-white"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
